/* eslint-disable react/react-in-jsx-scope */
import {
  Box,
  Grid,
  IconButton,
  Modal,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MapComponent from "../map-component/map-component";
import { ReactComponent as MobileIcon } from "../../../assets/icons/phone.svg";
import { ReactComponent as MailIcon } from "../../../assets/icons/mail-icon.svg";
import "./ProviderCardContactModal.scss";

interface ProviderCardContactModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  address: any;
  latitude: number;
  longitude: number;
  phoneNumber: string;
  email: string;
  contactInfo: any;
  isPlanner?: boolean;
}

export default function ProviderCardContactModal({
  open,
  onClose,
  title,
  address,
  latitude,
  longitude,
  phoneNumber,
  email,
  contactInfo,
  isPlanner,
}: ProviderCardContactModalProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
    >
      <Box
        className="modal-content"
        sx={{
          width: isMobile ? "80%" : "100%",
        }}
      >
        {/* Header */}
        <Box
          className="modal-header"
          sx={{
            display: "flex",
            justifyContent: isMobile ? "space-between" : "center",
            alignItems: "center",
            mb: isMobile ? 0 : 5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: isMobile ? "22px" : "25px",
              color: "#121212",
            }}
          >
            Contact Info
          </Typography>

          <IconButton
            onClick={onClose}
            sx={{
              ...(isMobile
                ? {
                    padding: 0,
                  }
                : {
                    position: "absolute",
                    right: -30,
                    top: "40%",
                    transform: "translateY(-50%)",
                  }),
            }}
          >
            <CloseIcon
              sx={{
                color: "#104145",
                width: "30px",
                height: "30px",
              }}
              fontSize="large"
            />
          </IconButton>
        </Box>

        {/* Title */}
        <Box className="title-box-component">
          <Typography
            sx={{
              // mt: isMobile ? 2 : 4,
              fontWeight: 600,
              fontSize: isMobile ? "18px" : "29px",
              mb: isMobile ? "0px !important" : "",
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Main Body */}
        {isMobile ? (
          // MOBILE VIEW
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Location Card */}
            <Box
              sx={{
                border: "0.5px solid #CED4DA",
                borderRadius: 1.5,
                padding: 1,
                boxShadow: "0 0 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontSize: "16px" }}
              >
                Location
              </Typography>
              <Box sx={{ borderBottom: "1px solid #CED4DA", mb: 2 }} />

              <Typography sx={{ fontSize: "14px", mb: 2 }}>
                {address?.street_line_1}, {address?.street_line_2},{" "}
                {address?.city}, {address?.state} {address?.zipcode},{" "}
                {address?.country}
              </Typography>

              <Box
                sx={{
                  backgroundColor: "#E0E0E0",
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <MapComponent latitude={latitude} longitude={longitude} />
              </Box>
            </Box>

            {/* Contact Info Card */}
            <Box
              sx={{
                border: "0.5px solid #CED4DA",
                borderRadius: 1.5,
                p: 1,
                boxShadow: "0 0 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontSize: "18px", mb: 0.1 }}
              >
                Contact Information
              </Typography>
              <Box sx={{ borderBottom: "1px solid #CED4DA", mb: 2 }} />

              {/* 🔹 Two-column layout */}
              <Grid container spacing={0.3}>
                <Grid item xs={6}>
                  <Box className="contact-box">
                    <SvgIcon
                      component={MobileIcon}
                      inheritViewBox
                      sx={{ width: 24, fill: "none", color: "blue !important" }}
                    />
                    <Box>
                      <Typography variant="body2" className="contact-label">
                        Phone Number
                      </Typography>
                      <Typography className="contact-value">
                        {phoneNumber}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box className="contact-box">
                    <SvgIcon
                      component={MobileIcon}
                      inheritViewBox
                      sx={{ width: 24, fill: "none" }}
                    />
                    <Box>
                      <Typography variant="body2" className="contact-label">
                        Emergency Help Line
                      </Typography>

                      {contactInfo.emergencyContactPhone ? (
                        <Typography
                          className="contact-value"
                          component="a"
                          href={`tel:${contactInfo.emergencyContactPhone}`}
                          sx={{
                            textDecoration: "none",
                            color: "inherit",
                            cursor: "pointer",
                          }}
                        >
                          {contactInfo.emergencyContactPhone}
                        </Typography>
                      ) : (
                        <Typography
                          className="contact-value"
                          sx={{
                            color: "text.disabled",
                            cursor: "default",
                          }}
                        >
                          -
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box className="contact-box">
                    <SvgIcon
                      component={MailIcon}
                      inheritViewBox
                      sx={{ width: 24, fill: "none" }}
                    />
                    <Box>
                      <Typography variant="body2" className="contact-label">
                        Email
                      </Typography>
                      <Typography className="contact-value">{email}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Business Hours Card */}
            <Box
              sx={{
                border: "0.5px solid #CED4DA",
                borderRadius: 1.5,
                p: 1,
                boxShadow: "0 0 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontSize: "18px" }}
              >
                Business Hours
              </Typography>
              <Box sx={{ borderBottom: "1px solid #CED4DA", mb: 2 }} />
              <Grid container>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: "16px" }}
                  >
                    Monday- Friday
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "16px", textAlign: "end" }}
                  >
                    9:30 AM - 8:30 PM
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        ) : (
          // DESKTOP VIEW
          <Grid
            container
            spacing={2}
            columnSpacing={5}
            sx={{ mt: 4 }}
            className="desktop-grid-component"
          >
            {/* Location */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                border: "0.5px solid #CED4DA",
                borderRadius: 1.5,
                p: 2,
                paddingLeft: "2.5rem !important",
              }}
              className="desktop-grid-item"
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontSize: "20px", mb: 1 }}
              >
                Location
              </Typography>
              <Box sx={{ borderBottom: "1px solid #CED4DA", mb: 2 }} />

              <Typography
                sx={{ fontSize: "16px", mb: 2 }}
                className="location-address--clamp"
              >
                {address?.street_line_1}, {address?.street_line_2},{" "}
                {address?.city}, {address?.state} {address?.zipcode},{" "}
                {address?.country}
              </Typography>

              <Box
                sx={{
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <MapComponent
                  latitude={latitude}
                  longitude={longitude}
                  height="260px"
                />
              </Box>
            </Grid>

            {/*  Contact Info + Business Hours */}
            <Grid item xs={12} md={6}>
              {/* Contact Info */}
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontSize: "20px", mb: 1 }}
              >
                Contact Information
              </Typography>
              <Box sx={{ borderBottom: "1px solid #CED4DA", mb: 2 }} />

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Box className="contact-box">
                    <SvgIcon
                      component={MobileIcon}
                      inheritViewBox
                      sx={{ width: 24, mr: 1, fill: "none" }}
                    />
                    <Box>
                      <Typography variant="body2" className="contact-label">
                        Phone Number
                      </Typography>
                      <Typography
                        className="contact-value"
                        component="a"
                        href={`tel:${phoneNumber}`}
                        sx={{
                          textDecoration: "none",
                          color: "inherit",
                          cursor: "pointer",
                        }}
                      >
                        {phoneNumber}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {!isPlanner && (
                  <Grid item xs={6}>
                    <Box className="contact-box">
                      <SvgIcon
                        component={MobileIcon}
                        inheritViewBox
                        sx={{ width: 24, mr: 1, fill: "none" }}
                      />
                      <Box>
                        <Typography variant="body2" className="contact-label">
                          Emergency Help Line
                        </Typography>
                        <Typography
                          className="contact-value"
                          component="a"
                          href={
                            contactInfo.emergencyContactPhone
                              ? `tel:${contactInfo.emergencyContactPhone}`
                              : undefined
                          }
                          sx={{
                            textDecoration: "none",
                            color: contactInfo.emergencyContactPhone
                              ? "inherit"
                              : "text.disabled",
                            cursor: contactInfo.emergencyContactPhone
                              ? "pointer"
                              : "default",
                          }}
                        >
                          {contactInfo.emergencyContactPhone ?? "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={6}>
                  <Box className="contact-box">
                    <SvgIcon
                      component={MailIcon}
                      inheritViewBox
                      sx={{ width: 24, mr: 1, fill: "none" }}
                    />
                    <Box>
                      <Typography variant="body2" className="contact-label">
                        Email
                      </Typography>
                      <Typography
                        className="contact-value"
                        component="a"
                        href={`mailto:${email}`}
                        sx={{
                          textDecoration: "none",
                          color: "inherit",
                          cursor: "pointer",
                        }}
                      >
                        {email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Business Hours */}
              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontSize: "20px" }}
                >
                  Business Hours
                </Typography>
                <Box sx={{ borderBottom: "1px solid #CED4DA", mb: 2 }} />
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: "20px" }}
                    >
                      Monday- Friday
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "18px", textAlign: "end" }}
                    >
                      9:30 AM - 8:30 PM
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Modal>
  );
}
